/**
 * KartotekaFieldsManager — reużywalny panel zarządzania polami kartoteki systemowej.
 *
 * Jedno źródło UI dla dwóch miejsc (DRY):
 *  - edytor szablonu modułu (zakładka „Pola" encji-odwołania) — E2.2b,
 *  - strona „Kartoteki" (modal zarządzania kartoteką systemową) — E2.2c.
 *
 * Renderuje trzy zakładki:
 *  1. „Pola kartoteki" — lista pól (rdzeń + dodane przez ITE) + przycisk „Dodaj pole".
 *  2. „Layouty formularzy" — CRUD (Etap 3 T3.1, `KartotekaFormViewsPanel`): tworzenie,
 *     zmiana nazwy, duplikacja, usuwanie (guard publikacji) + edycja układu embedded
 *     builderem (`KartotekaViewFormBuilder` — reużywa `ModuleBuilderPage`).
 *  3. „Widoki list" — readonly lista widoków listy z biblioteki widoków (poza zakresem T3.1).
 *
 * Klucze i18n współdzielone z dawnym panelem: `moduleTemplateEditor.referencePanel.*`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Lock, Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AddKartotekaFieldModal } from '@/components/kartoteki/AddKartotekaFieldModal';
import { KartotekaFormViewsPanel } from '@/components/kartoteki/KartotekaFormViewsPanel';
import { KartotekaListViewsPanel } from '@/components/kartoteki/KartotekaListViewsPanel';
import { Skeleton } from '@/components/Skeleton';
import { Tabs } from '@/components/Tabs';
import { useToast } from '@/contexts/ToastContext';
import {
  applyKartotekaToTenants,
  deleteKartotekaField,
  type KartotekaApplyResult,
  type KartotekaBuilderField,
  useKartotekaDefinition,
  useKartotekaSystemViews,
} from '@/lib/kartoteki/kartoteka-definition';

/** Formatuje datę dodania pola (ISO → czytelna data pl-PL). Pusty string przy błędnej dacie. */
function formatAddedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pl-PL');
}

/**
 * Buduje treść ostrzeżenia częściowego sukcesu fleet-push — łączy tylko klauzule,
 * których licznik jest > 0 (porażki tenantów / błędy widoków / nieprawidłowe widoki),
 * żeby nie pokazywać zerowych fraz typu „widoki z błędem u 0 klientów".
 */
function buildPartialWarningDetail(
  t: ReturnType<typeof useTranslation>['t'],
  totals: KartotekaApplyResult['totals'],
): string {
  const parts: string[] = [];
  if (totals.failed > 0) {
    parts.push(
      t('moduleTemplateEditor.referencePanel.applyToTenants.partialWarningDetailFailed', {
        failed: totals.failed,
      }),
    );
  }
  if (totals.viewsFailed > 0) {
    parts.push(
      t('moduleTemplateEditor.referencePanel.applyToTenants.partialWarningDetailViewsFailed', {
        viewsFailed: totals.viewsFailed,
      }),
    );
  }
  if (totals.viewsInvalid > 0) {
    parts.push(
      t('moduleTemplateEditor.referencePanel.applyToTenants.partialWarningDetailViewsInvalid', {
        viewsInvalid: totals.viewsInvalid,
      }),
    );
  }
  return parts.join(' ');
}

// ─── Tab: Pola kartoteki ──────────────────────────────────────────────────────

/** Pojedynczy wiersz pola. `showDelete` gate'uje kosz (sekcja odziedziczona = zawsze read-only). */
function FieldRow({
  field: f,
  showDelete,
  onDeleteClick,
}: {
  field: KartotekaBuilderField;
  showDelete: boolean;
  onDeleteClick: (f: KartotekaBuilderField) => void;
}) {
  const { t } = useTranslation();
  return (
    <li
      className="flex flex-wrap items-center gap-2 py-1.5 text-sm"
      data-testid="reference-field-item"
    >
      <Lock
        className="h-3.5 w-3.5 shrink-0 text-gray-400"
        aria-label={t('moduleTemplateEditor.referencePanel.lockedHint')}
      />
      <span className="text-text-primary">{f.name}</span>
      <code className="text-xs text-gray-400">{f.slug}</code>
      {f.isCore ? (
        <Badge color="gray">{t('moduleTemplateEditor.referencePanel.coreBadge')}</Badge>
      ) : (
        <>
          <Badge color="green">{t('moduleTemplateEditor.referencePanel.addedFieldBadge')}</Badge>
          {/* Autor + data — luźnym tekstem obok badge'a (nie w badge'u). */}
          <span className="text-xs text-gray-400">
            {t('moduleTemplateEditor.referencePanel.addedByText', {
              name: f.addedByName ?? t('moduleTemplateEditor.referencePanel.addedByUnknown'),
            })}
            {f.addedAt ? ` · ${formatAddedAt(f.addedAt)}` : ''}
          </span>
        </>
      )}
      <code className="ml-auto text-xs text-gray-500 dark:text-gray-400">{f.fieldType}</code>
      {showDelete && !f.isCore && !f.firstPublishedAt && (
        <button
          type="button"
          aria-label={t('moduleTemplateEditor.referencePanel.deleteFieldButton')}
          data-testid="kartoteka-delete-field-btn"
          className="ml-1 rounded p-0.5 text-gray-400 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          onClick={() => onDeleteClick(f)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}

/** Przycisk „Dodaj pole" — współdzielony przez widok klasy i sekcję „Pola tego typu". */
function AddFieldButton({ onAddClick }: { onAddClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Button
      size="sm"
      variant="secondary"
      data-testid="kartoteka-add-field-btn"
      onClick={onAddClick}
    >
      <Plus className="mr-1 h-4 w-4" />
      {t('moduleTemplateEditor.referencePanel.addFieldButton')}
    </Button>
  );
}

interface FieldsTabPanelProps {
  readOnly: boolean;
  /** Tryb typu dokumentu — renderuje dwie sekcje: odziedziczone (read-only) + własne typu. */
  typeMode: boolean;
  definitionQuery: ReturnType<typeof useKartotekaDefinition>;
  fields: KartotekaBuilderField[];
  onAddClick: () => void;
  onDeleteClick: (f: KartotekaBuilderField) => void;
}

function FieldsTabPanel({
  readOnly,
  typeMode,
  definitionQuery,
  fields,
  onAddClick,
  onDeleteClick,
}: FieldsTabPanelProps) {
  const { t } = useTranslation();

  const loading = definitionQuery.isLoading;
  const errored = definitionQuery.isError;

  // Widok TYPU: pola z klasy (odziedziczone, read-only) + pola własne tego typu (edytowalne).
  if (typeMode) {
    const inheritedFields = fields.filter((f) => f.inherited);
    const ownFields = fields.filter((f) => !f.inherited);
    return (
      <div className="space-y-6">
        {loading && <Skeleton className="h-24 w-full" />}
        {errored && (
          <p className="text-sm text-red-600 dark:text-red-400">
            {t('moduleTemplateEditor.referencePanel.loadError')}
          </p>
        )}

        {!loading && !errored && (
          <>
            <section>
              <h4 className="mb-2 text-sm font-semibold text-text-secondary">
                {t('moduleTemplateEditor.referencePanel.inheritedSectionTitle')}
              </h4>
              {inheritedFields.length === 0 ? (
                <p className="text-sm text-text-muted">
                  {t('moduleTemplateEditor.referencePanel.noFields')}
                </p>
              ) : (
                <ul
                  className="divide-y divide-gray-100 dark:divide-gray-700"
                  data-testid="kartoteka-inherited-fields"
                >
                  {inheritedFields.map((f) => (
                    <FieldRow
                      key={f.id}
                      field={f}
                      showDelete={false}
                      onDeleteClick={onDeleteClick}
                    />
                  ))}
                </ul>
              )}
            </section>

            <section>
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-text-secondary">
                  {t('moduleTemplateEditor.referencePanel.ownTypeSectionTitle')}
                </h4>
                {!readOnly && <AddFieldButton onAddClick={onAddClick} />}
              </div>
              {ownFields.length === 0 ? (
                <p className="text-sm text-text-muted">
                  {t('moduleTemplateEditor.referencePanel.noFields')}
                </p>
              ) : (
                <ul
                  className="divide-y divide-gray-100 dark:divide-gray-700"
                  data-testid="kartoteka-own-type-fields"
                >
                  {ownFields.map((f) => (
                    <FieldRow
                      key={f.id}
                      field={f}
                      showDelete={!readOnly}
                      onDeleteClick={onDeleteClick}
                    />
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    );
  }

  // Widok KLASY (bez zmian): pojedyncza lista pól + „Dodaj pole".
  return (
    <div>
      {!readOnly && (
        <div className="mb-3 flex justify-end">
          <AddFieldButton onAddClick={onAddClick} />
        </div>
      )}

      {loading && <Skeleton className="h-24 w-full" />}
      {errored && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {t('moduleTemplateEditor.referencePanel.loadError')}
        </p>
      )}

      {!loading && !errored && (
        <>
          {fields.length === 0 ? (
            <p className="text-sm text-text-muted">
              {t('moduleTemplateEditor.referencePanel.noFields')}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {fields.map((f) => (
                <FieldRow
                  key={f.id}
                  field={f}
                  showDelete={!readOnly}
                  onDeleteClick={onDeleteClick}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

interface KartotekaFieldsManagerProps {
  /** Slug kartoteki systemowej (np. 'contractors'). W trybie typu = slug klasy-rodzica. */
  slug: string;
  /** Read-only (np. opublikowany szablon) — bez przycisku „Dodaj pole". */
  readOnly?: boolean;
  /**
   * Pokazuje przycisk fleet-push „Wyślij do klientów" (M4/T3.4 — POST .../apply).
   * Wyłącznie dla kontekstu zarządzania KARTOTEKĄ SYSTEMOWĄ (rejestr ITE —
   * `AdminKartoteki`); w edytorze szablonu (encja-odwołanie) NIE ustawiaj —
   * tam obowiązują odrębne, per-widok przyciski „Wyślij layout"/„Wyślij widok listy".
   */
  canApplyToTenants?: boolean;
  /**
   * Widok TYPU dokumentu (drzewko klasa → typ). Gdy ustawione: zakładka „Pola" renderuje
   * dwie sekcje — pola z klasy (odziedziczone, read-only) + pola tego typu (edytowalne);
   * „Dodaj pole" tworzy pole prywatne dla tego typu.
   */
  typeId?: string;
}

export function KartotekaFieldsManager({
  slug,
  readOnly = false,
  canApplyToTenants = false,
  typeId,
}: KartotekaFieldsManagerProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const definitionQuery = useKartotekaDefinition(slug, typeId);
  const fields = definitionQuery.data?.fields ?? [];
  // Gotowość katalogu (zapytanie ma potwierdzone dane, nie fallback `[]` w trakcie
  // ładowania) — forward do KartotekaListViewsPanel → KartotekaViewListBuilder, żeby
  // soft-heal w ListColumnsPanel nie wycinał kolumn zanim katalog pól się wczyta.
  const fieldsReady = definitionQuery.data !== undefined;

  const viewsQuery = useKartotekaSystemViews(slug);

  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<KartotekaBuilderField | null>(null);
  const [confirmApplyOpen, setConfirmApplyOpen] = useState(false);

  const applyMutation = useMutation({
    mutationFn: () => applyKartotekaToTenants(slug),
    onSuccess: (data: KartotekaApplyResult) => {
      const { totals } = data;
      const hasFailures = totals.failed > 0 || totals.viewsFailed > 0 || totals.viewsInvalid > 0;
      toast.show({
        variant: hasFailures ? 'warning' : 'success',
        title: hasFailures
          ? t('moduleTemplateEditor.referencePanel.applyToTenants.partialWarning', {
              failed: totals.failed,
              tenants: totals.tenants,
            })
          : t('moduleTemplateEditor.referencePanel.applyToTenants.success', {
              succeeded: totals.succeeded,
              tenants: totals.tenants,
            }),
        description: hasFailures
          ? buildPartialWarningDetail(t, totals)
          : t('moduleTemplateEditor.referencePanel.applyToTenants.successDetail', {
              fieldsAdded: totals.fieldsAdded,
              created: totals.viewsCreated,
              updated: totals.viewsUpdated,
              removed: totals.viewsRemoved,
            }),
      });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('moduleTemplateEditor.referencePanel.applyToTenants.error'),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (field: KartotekaBuilderField) => deleteKartotekaField(slug, field.id),
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('moduleTemplateEditor.referencePanel.deleteFieldSuccess'),
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['admin', 'kartoteki', slug, 'definition'],
        }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'kartoteki', slug] }),
      ]);
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      const messageKey =
        code === 'FIELD_PUBLISHED_CANNOT_DELETE'
          ? 'moduleTemplateEditor.referencePanel.deleteFieldPublishedError'
          : 'moduleTemplateEditor.referencePanel.deleteFieldError';
      toast.show({ variant: 'error', title: t(messageKey) });
    },
  });

  // Tab label with optional count (shown only when data is already loaded)
  const formViewsCount = viewsQuery.data?.filter((v) => v.viewType === 'form').length;
  const listViewsCount = viewsQuery.data?.filter((v) => v.viewType === 'list').length;

  function withCount(base: string, count: number | undefined): string {
    return count !== undefined ? `${base} (${count})` : base;
  }

  const tabItems = [
    {
      value: 'fields',
      label: withCount(
        t('moduleTemplateEditor.referencePanel.tabFields'),
        definitionQuery.data ? fields.length : undefined,
      ),
      content: (
        <FieldsTabPanel
          readOnly={readOnly}
          typeMode={Boolean(typeId)}
          definitionQuery={definitionQuery}
          fields={fields}
          onAddClick={() => setAddOpen(true)}
          onDeleteClick={setPendingDelete}
        />
      ),
    },
    {
      value: 'formViews',
      label: withCount(t('moduleTemplateEditor.referencePanel.tabFormViews'), formViewsCount),
      content: (
        <KartotekaFormViewsPanel
          identity={{ kind: 'system', key: slug }}
          fields={fields}
          viewsQuery={viewsQuery}
          readOnly={readOnly}
        />
      ),
    },
    {
      value: 'listViews',
      label: withCount(t('moduleTemplateEditor.referencePanel.tabListViews'), listViewsCount),
      content: (
        <KartotekaListViewsPanel
          identity={{ kind: 'system', key: slug }}
          fields={fields}
          fieldsReady={fieldsReady}
          viewsQuery={viewsQuery}
          readOnly={readOnly}
          defaultListConfig={definitionQuery.data?.defaultListConfig}
        />
      ),
    },
  ];

  return (
    <div data-testid="kartoteka-fields-manager">
      {canApplyToTenants && (
        <div className="mb-4 flex justify-end">
          <Button
            size="sm"
            variant="primary"
            data-testid="kartoteka-send-to-tenants-btn"
            disabled={applyMutation.isPending}
            onClick={() => setConfirmApplyOpen(true)}
          >
            {t('moduleTemplateEditor.referencePanel.applyToTenants.button')}
          </Button>
        </div>
      )}

      <Tabs idPrefix={`kartoteka-${slug}`} defaultValue="fields" items={tabItems} />

      <AddKartotekaFieldModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        kartotekaSlug={slug}
        documentTypeId={typeId}
      />

      <ConfirmDialog
        open={confirmApplyOpen}
        variant="primary"
        title={t('moduleTemplateEditor.referencePanel.applyToTenants.confirmTitle')}
        message={t('moduleTemplateEditor.referencePanel.applyToTenants.confirmMessage')}
        confirmLabel={t('moduleTemplateEditor.referencePanel.applyToTenants.button')}
        onConfirm={() => {
          setConfirmApplyOpen(false);
          applyMutation.mutate();
        }}
        onClose={() => setConfirmApplyOpen(false)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('moduleTemplateEditor.referencePanel.deleteFieldConfirmTitle')}
        message={
          pendingDelete
            ? t('moduleTemplateEditor.referencePanel.deleteFieldConfirmMessage', {
                name: pendingDelete.name,
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) {
            deleteMutation.mutate(pendingDelete);
            setPendingDelete(null);
          }
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
